'use client'

import { InstructionSize } from '@/app/[locale]/product/[slug]/_components/product-info-sizes/instructions-size'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useIsMobileOrTabletClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { ProductFormatted } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { useBirthdayPreOrder } from '../_hooks/use-birthday-pre-order'

const COLOR_IMAGES: Record<string, string> = {
  Trắng: '/image/2026/03/16/Frame_88136.png',
  Đen: '/image/2026/03/16/Frame_88135.png',
}

interface BirthdayPreOrderClientProps {
  product: ProductFormatted
}

export const BirthdayPreOrderClient = ({
  product,
}: BirthdayPreOrderClientProps) => {
  const isMobile = useIsMobileOrTabletClient()
  const searchParams = useSearchParams()
  const scrollRef = useRef<HTMLDivElement>(null)

  const {
    step,
    setStep,
    selectedColor,
    setSelectedColor,
    selectedSize,
    setSelectedSize,
    customName,
    onCustomNameChange,
    colors,
    sizes,
    register,
    handleSubmit,
    errors,
    nameError,
    handleNextToForm,
    handleClosePopup,
    onFormSubmit,
    isSubmitting,
  } = useBirthdayPreOrder({ product })

  useEffect(() => {
    const scrollToPreOrder = searchParams.get('scroll-to-pre-order') === 'true'
    if (scrollToPreOrder && scrollRef.current) {
      const timer = setTimeout(() => {
        scrollRef.current?.scrollIntoView({
          behavior: 'smooth',
        })
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [searchParams])

  const previewName = customName || '[TÊN]'

  return (
    <div
      id='scroll-to-pre-order'
      className='scroll-mt-25 md:scroll-mt-30 w-full'
      ref={scrollRef}
    >
      <div className='relative overflow-hidden rounded-3xl bg-[url(https://mcdn.coolmate.me/image/2026/03/17/Frame_88006.jpg)] bg-cover p-6 text-white md:p-15 2xl:px-44'>
        <div className='relative z-10 grid grid-cols-1 gap-8 md:gap-20 lg:grid-cols-12'>
          {/* Left Content */}
          <div className='flex flex-col justify-center text-left md:max-w-2xl lg:col-span-7'>
            <h1 className='mb-3 font-criteria text-3xl font-semibold !leading-normal tracking-tight md:text-5xl lg:text-[55px]'>
              TẠO DẤU ẤN MATE <br className='hidden md:block' /> CỦA RIÊNG BẠN
            </h1>
            <p className='mb-2 text-sm italic leading-relaxed opacity-80 md:mb-6 md:text-base 2xl:mb-10 2xl:text-xl'>
              Cảm ơn bạn đã trở thành một phần của Coolmate. Lần này, spotlight
              trong logo thuộc về chính bạn. Hãy biến logo Coolmate thành dấu ấn
              mang tên bạn.
            </p>

            {isMobile ? (
              <div className='mb-3 flex items-center justify-center'>
                <div className='relative aspect-[620/734] w-full max-w-[620px]'>
                  <Image
                    src={ImageHelper.getN7ImageUrl(COLOR_IMAGES[selectedColor])}
                    alt={product.title}
                    fill
                    className='object-contain'
                    priority
                  />
                  {/* Name Overlay */}
                  <div className='absolute inset-0 flex justify-center pt-[52%]'>
                    <div
                      className={cn(
                        'text-[8px] font-bold uppercase',
                        selectedColor === 'Đen'
                          ? 'text-white'
                          : 'text-neutral-800'
                      )}
                    >
                      {previewName}{' '}
                      <span
                        className={cn(
                          selectedColor === 'Đen'
                            ? 'bg-white text-black'
                            : 'bg-black text-white',
                          'rounded-sm px-1'
                        )}
                      >
                        MATE
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className='mb-3 md:mb-6'>
              <Input
                label={<span className='text-white'>Điền tên của bạn</span>}
                value={customName}
                required
                onChange={(e) => onCustomNameChange(e.target.value.slice(0, 7))}
                placeholder='Nhập tên của bạn'
                helpText='Tối đa 7 kí tự'
                error={nameError ? { message: nameError } : undefined}
                wrapperClassName='text-white'
                className='mt-3'
              />
            </div>

            {/* <div className='mb-3 2xl:mb-6'>
              <p className='mb-3 text-left text-sm font-semibold'>Màu sắc:</p>
              <div
                className='grid gap-2.5 gap-y-1.5 md:flex md:flex-wrap lg:gap-3 lg:gap-y-3'
                style={{
                  gridTemplateColumns: `repeat(4, minmax(0, 1fr))`,
                }}
              >
                {colors.map((color) => {
                  const isActive = selectedColor === color
                  const imageThumb =
                    product.mapped_variants[color]?.[0]?.colorImage
                  return (
                    <button
                      key={color}
                      onClick={() => {
                        setSelectedColor(color)
                      }}
                      className={cn(
                        'relative h-9 w-16 overflow-hidden rounded-full border-2 transition-all md:h-[38px] md:w-[71px]',
                        isActive
                          ? 'border-white ring-2 ring-white/30'
                          : 'border-transparent'
                      )}
                      title={color}
                    >
                      <Image
                        src={ImageHelper.getN7ImageUrl(imageThumb || '')}
                        alt={`color ${color}`}
                        fill
                        className='object-cover'
                        unoptimized
                      />
                    </button>
                  )
                })}
              </div>
            </div> */}

            <div className='mb-5 md:mb-10'>
              <div className='mb-3 flex items-center justify-between'>
                <p className='text-sm font-semibold'>Kích thước:</p>
                {product.sizechart && (
                  <InstructionSize
                    sizeChart={product.sizechart || ''}
                    gender_type={product.gender_type || ''}
                    size_guide={
                      product.size_guide || {
                        rows: [],
                        columns: [],
                        headerTitle: '',
                        leftTitle: '',
                        matrix: [],
                      }
                    }
                    productId={product.id}
                    product={product as unknown as ProductDetailFormatted}
                    className='text-white'
                  />
                )}
              </div>
              <ul className='flex flex-wrap gap-2 md:gap-3'>
                {sizes.map(({ size, isDisable }) => (
                  <li key={size}>
                    <button
                      onClick={() => !isDisable && setSelectedSize(size)}
                      disabled={isDisable}
                      className={cn(
                        'relative flex h-9 w-13 items-center justify-center rounded-2xl font-medium transition-all md:h-11 md:w-16',
                        selectedSize === size
                          ? 'bg-black text-white'
                          : 'bg-white text-black hover:bg-neutral-100',
                        isDisable &&
                          'pointer-events-none cursor-not-allowed !bg-neutral-100/50 !text-neutral-300 before:absolute before:left-0 before:top-1/2 before:w-full before:rotate-[30deg] before:border-t before:border-dashed before:border-neutral-300 after:absolute after:left-0 after:top-1/2 after:w-full after:rotate-[145deg] after:border-t after:border-dashed after:border-neutral-300'
                      )}
                    >
                      {size}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <Button variant='black' size='large' onClick={handleNextToForm}>
              ĐẶT MUA NGAY
            </Button>

            <i className='mt-3 text-xs opacity-80 md:text-sm'>
              *Vì đây là sản phẩm in theo yêu cầu, nên sản phẩm này sẽ không áp
              dụng đổi trả hàng hóa Nếu sản phẩm bị lỗi bong/tróc hình in, vui
              lòng nhắn CSKH để hỗ trợ xử lý ạ
            </i>
          </div>

          {/* Right Card */}
          {!isMobile ? (
            <div className='flex items-center justify-center lg:col-span-5'>
              <div className='relative aspect-[620/734] w-full max-w-[620px]'>
                <Image
                  src={ImageHelper.getN7ImageUrl(COLOR_IMAGES[selectedColor])}
                  alt={product.title}
                  fill
                  className='object-contain'
                  priority
                />
                {/* Name Overlay */}
                <div className='absolute inset-0 flex justify-center pt-[52%]'>
                  <div
                    className={cn(
                      'text-[8px] font-bold uppercase md:text-base',
                      selectedColor === 'Đen'
                        ? 'text-white'
                        : 'text-neutral-800'
                    )}
                  >
                    {previewName}{' '}
                    <span
                      className={cn(
                        selectedColor === 'Đen'
                          ? 'bg-white text-black'
                          : 'bg-black text-white',
                        'rounded-sm px-1'
                      )}
                    >
                      MATE
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Popup Form (Step 2) */}
      <Dialog
        open={step === 'FORM'}
        onOpenChange={(open) => !open && handleClosePopup()}
      >
        <DialogTitle />
        <DialogContent className='max-w-sm overflow-hidden !rounded-2xl border-none p-0'>
          <div className='bg-white px-4 py-8'>
            <div className='mb-5 flex items-center justify-between'>
              <h2 className='font-criteria text-2xl font-semibold text-primary'>
                Gửi yêu cầu cho CoolxPrint
              </h2>
            </div>

            <form
              onSubmit={handleSubmit(onFormSubmit)}
              className='grid grid-cols-1 gap-3 text-left'
            >
              <Input
                label='Sản phẩm và số lượng dự kiến bạn cần'
                isRequired
                placeholder='Ví dụ: 3 áo'
                {...register('quantity_product', {
                  required: 'Vui lòng nhập sản phẩm và số lượng',
                })}
                error={errors.quantity_product}
              />
              <Input
                label='Họ và tên của bạn'
                isRequired
                placeholder='Nhập họ tên của bạn'
                {...register('name', { required: 'Vui lòng nhập họ tên' })}
                error={errors.name}
              />
              <Input
                label='Số điện thoại của bạn'
                isRequired
                placeholder='Nhập số điện thoại của bạn'
                {...register('phone', {
                  required: 'Vui lòng nhập số điện thoại',
                  pattern: {
                    value: /^[0-9]{10,11}$/,
                    message: 'Số điện thoại không hợp lệ',
                  },
                })}
                error={errors.phone}
              />
              <Input
                label='Email của bạn'
                isRequired
                placeholder='Nhập email của bạn'
                {...register('email', {
                  required: 'Vui lòng nhập email',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Email không hợp lệ',
                  },
                })}
                error={errors.email}
              />
              <Button
                type='submit'
                variant='primary'
                className='mt-2 w-fit'
                size='large'
                disabled={isSubmitting}
              >
                {isSubmitting ? 'ĐANG GỬI...' : 'GỬI YÊU CẦU'}
              </Button>
              <i className='text-xs opacity-80 md:text-sm'>
                *Vì đây là sản phẩm in theo yêu cầu, nên sản phẩm này sẽ không
                áp dụng đổi trả hàng hóa Nếu sản phẩm bị lỗi bong/tróc hình in,
                vui lòng nhắn CSKH để hỗ trợ xử lý ạ
              </i>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Popup (Step 3) */}
      <Dialog
        open={step === 'SUCCESS'}
        onOpenChange={(open) => !open && setStep('PRODUCT')}
      >
        <DialogTitle />
        <DialogContent className='max-w-sm overflow-hidden !rounded-2xl border-none p-0 md:max-w-md'>
          <div className='flex flex-col items-center justify-center bg-white p-10 text-center'>
            <div className='mb-5 flex h-14 w-14 items-center justify-center'>
              <Image
                src={ImageHelper.getN7ImageUrl(
                  '/image/2026/03/10/checkmark_1.png'
                )}
                alt='success'
                width={56}
                height={56}
              />
            </div>
            <h2 className='mb-2 font-criteria text-lg font-medium text-neutral-900 md:text-xl'>
              Thông tin của bạn đã được gửi đi!
            </h2>
            <p className='mx-auto mb-3 text-sm text-neutral-500 md:mb-6 md:text-base'>
              Đội ngũ CoolxPrint sẽ liên hệ lại với bạn trong 24h.
            </p>
            <Button variant='black' onClick={() => setStep('PRODUCT')}>
              Tiếp tục mua sắm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
